import { useCallback, useEffect, useState } from "react";
import "@/App.css";
import Lenis from "lenis";
import { Toaster } from "@/components/ui/sonner";
import { initTracking, trackEvent, trackViewContent } from "@/lib/track";
import Header from "@/components/landing/Header";
import Hero from "@/components/landing/Hero";
import TrustStrip from "@/components/landing/TrustStrip";
import Marquee from "@/components/landing/Marquee";
import ParentSection from "@/components/landing/ParentSection";
import WhySection from "@/components/landing/WhySection";
import NadiPariksha from "@/components/landing/NadiPariksha";
import BrainSystems from "@/components/landing/BrainSystems";
import BrainTypeCta from "@/components/landing/BrainTypeCta";
import Packages from "@/components/landing/Packages";
import NotSure from "@/components/landing/NotSure";
import Doctor from "@/components/landing/Doctor";
import HowItWorks from "@/components/landing/HowItWorks";
import Videos from "@/components/landing/Videos";
import Testimonials from "@/components/landing/Testimonials";
import Availability from "@/components/landing/Availability";
import LeadForm from "@/components/landing/LeadForm";
import Faq from "@/components/landing/Faq";
import FinalCta from "@/components/landing/FinalCta";
import Footer from "@/components/landing/Footer";
import StickyCta from "@/components/landing/StickyCta";
import AssessmentModal from "@/components/landing/AssessmentModal";

function App() {
  const [selectedPackage, setSelectedPackage] = useState("");
  const [assessOpen, setAssessOpen] = useState(false);
  const [assessLead, setAssessLead] = useState(null);

  const openAssessment = useCallback(() => {
    trackEvent("AssessmentCTA", { label: "open_assessment" });
    trackViewContent({ content_name: "20-Question Brain Pattern Assessment" });
    setAssessOpen(true);
  }, []);

  const startAssessmentWithLead = useCallback(
    (lead) => {
      setAssessLead(lead);
      openAssessment();
    },
    [openAssessment]
  );

  const requestConsultation = useCallback((pkg = "") => {
    if (pkg) {
      setSelectedPackage(pkg);
      trackEvent("PackageSelect", { package: pkg });
    }
    trackEvent("CTAClick", { label: pkg ? "package_cta" : "generic_cta" });
    const el = document.getElementById("lead-form");
    if (!el) return;
    if (window.__lenis) window.__lenis.scrollTo(el, { offset: -70 });
    else el.scrollIntoView({ behavior: "smooth" });
  }, []);

  useEffect(() => {
    initTracking();
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    window.__lenis = lenis;
    let raf;
    const loop = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);

    const marks = [25, 50, 75, 100];
    const hit = new Set();
    const onScroll = () => {
      const h = document.documentElement;
      const max = h.scrollHeight - h.clientHeight;
      if (max <= 0) return;
      const pct = Math.round((h.scrollTop / max) * 100);
      marks.forEach((m) => {
        if (pct >= m && !hit.has(m)) {
          hit.add(m);
          trackEvent("ScrollDepth", { percent: m });
        }
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      window.removeEventListener("scroll", onScroll);
    };
  }, []);

  return (
    <div className="App bg-[#FAF7F2]">
      <div className="grain-overlay" />
      <Header onRequest={requestConsultation} />
      <main>
        <Hero onRequest={requestConsultation} onDiscover={openAssessment} onLeadStarted={startAssessmentWithLead} />
        <TrustStrip />
        <Marquee />
        <ParentSection onRequest={requestConsultation} />
        <WhySection />
        <NadiPariksha />
        <BrainSystems />
        <BrainTypeCta onOpen={openAssessment} />
        <Packages onRequest={requestConsultation} />
        <NotSure onRequest={requestConsultation} />
        <Doctor onRequest={requestConsultation} />
        <HowItWorks onRequest={requestConsultation} />
        <Videos />
        <Testimonials />
        <Availability onRequest={requestConsultation} />
        <LeadForm selectedPackage={selectedPackage} />
        <Faq />
        <FinalCta onRequest={requestConsultation} />
      </main>
      <Footer />
      <div className="h-[72px] md:hidden" />
      <StickyCta onRequest={requestConsultation} />
      <AssessmentModal open={assessOpen} onClose={() => setAssessOpen(false)} linkedLead={assessLead} />
      <Toaster position="top-center" richColors />
    </div>
  );
}

export default App;
